import { apiClientVersioned, returnData } from '../index'

import type {
  IChat,
  IChatListFilters,
  IChatMessage,
  IChatPinsBody,
  IHtmlPagesResponse,
  IMessageFeedback,
  IMessageFeedbackRequest,
  IPaginatedChatListResponse,
} from '../types/chat'
import type { IWebsiteSummary } from '../types/websites'

export const ChatApi = {
  /**
   * The sidebar list. Page 1 also carries the caller's `pinned` chats, in their stored
   * order and excluded from `items` (see {@link IPaginatedChatListResponse}).
   */
  list: (params?: IChatListFilters) =>
    returnData(
      apiClientVersioned.get<IPaginatedChatListResponse>('/chats', {
        params,
      }),
    ),

  get: (
    id: IChat['id'],
    params?: {
      messages_limit?: number
      messages_offset?: number
      include_messages?: boolean
    },
  ) =>
    returnData(
      apiClientVersioned.get<IChat>(`/chats/${id}`, {
        params,
      }),
    ),
  /**
   * One message of a chat, enriched exactly like the messages of GET `/chats/{id}`:
   * signed file URLs with `file.metadata` (website link), `ui_hints` (incl. processing
   * step outcomes), `publishable_html_endpoints` and feedback. Lets the UI resolve a
   * page from a message outside the loaded window without paging the history in.
   * `404` when the chat isn't the user's or the message isn't in it.
   */
  getMessage: (chatId: IChat['id'], messageId: string) =>
    returnData(
      apiClientVersioned.get<IChatMessage>(
        `/chats/${chatId}/messages/${messageId}`,
      ),
    ),
  /**
   * All generated HTML page descriptors for a chat (EVGPT-1565), in document order,
   * independent of message pagination. Lightweight — no raw HTML.
   */
  htmlPages: (id: IChat['id']) =>
    returnData(
      apiClientVersioned.get<IHtmlPagesResponse>(`/chats/${id}/html-pages`),
    ),
  /**
   * The chat's website — the single page a chat publishes to (one website per chat),
   * or `null` when it has none. The authoritative answer to "what is this chat's
   * website": it reflects a publish/unpublish immediately, unlike scanning the chat's
   * HTML blocks for `file.metadata.website_id`, which could go stale (EVGPT-1511).
   * Subject to the caller's read permission on the chat.
   */
  website: (id: IChat['id']) =>
    returnData(
      apiClientVersioned.get<IWebsiteSummary | null>(`/chats/${id}/website`),
    ),
  update: (chatId: string, chat: Partial<IChat>) =>
    returnData(apiClientVersioned.patch<IChat>(`/chats/${chatId}`, chat)),
  /**
   * Resets the chat's `updated_at` to now, extending its retention window by another
   * `chat_retention_days` (default 30). Also protects the chat's referenced files from
   * the orphan-file purge, so no separate file re-upload is required.
   */
  extendRetention: (chatId: string) =>
    returnData(
      apiClientVersioned.post<IChat>(`/chats/${chatId}/extend-retention`),
    ),
  /**
   * Replaces the caller's pinned chat list with `chat_ids`, topmost first — pin, unpin
   * and reorder are all this one call. Being a declarative replace rather than a toggle
   * makes it idempotent: a retried or double-submitted request still lands on the state
   * the caller asked for. Pins live per user in their own table, so nothing about a chat
   * row changes and there is no `is_pinned` field to read back. Returns 204 No Content;
   * answers 404 for a chat the user does not own (or that is deleted) and 422 for
   * duplicate ids or a draft chat.
   */
  setPins: (body: IChatPinsBody) =>
    returnData(apiClientVersioned.patch<void>('/chats/pins', body)),
  remove: (chatId: string) =>
    returnData(apiClientVersioned.delete(`/chats/${chatId}`)),
  removeAll: () => returnData(apiClientVersioned.delete('/chats/')),
  createMessageFeedback: (
    chatId: string,
    messageId: string,
    data: IMessageFeedbackRequest,
  ) =>
    returnData(
      apiClientVersioned.post<IMessageFeedback>(
        `/chats/${chatId}/messages/${messageId}/feedback`,
        data,
      ),
    ),
}
